import { useEffect, useState } from 'react';
import { ArrowRight, MapPin } from 'lucide-react';
import { HOME } from '@/constants/testIds';

// Hero image rotation — cross-fades between the empty Highland trail
// landscape and the Juliet flagship moorland shot every 8 seconds so
// first-time visitors see both the world we sell into and the bike
// we sell. Generated 2026-03-01 via Gemini Nano Banana. Swap files on
// disk to change either shot; no code change required.
const HERO_SLIDES = [
  {
    src: '/hero-and-juliet/hero-highland-trail.png',
    alt: 'A rugged Highland gravel trail winding through open Perthshire moorland — dry-stone dyke, heather in bloom, a wooden fingerpost pointing into the glen, distant mountains under moody grey cloud with golden light breaking through.',
  },
  {
    src: '/hero-and-juliet/juliet-flagship-moorland.png',
    alt: 'The real EAHORA 2026 Juliet in sage green — the actual bike you receive — parked on a Highland gravel trail through blooming heather, dry-stone dyke to one side, layered Perthshire mountains under moody broken cloud with golden light.',
  },
];

export default function Hero() {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    // Respect reduced-motion — no auto-rotation for anyone who's asked
    // their OS to keep things still.
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_SLIDES.length);
    }, 8000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section
      id="hero"
      data-testid={HOME.heroSection}
      className="relative w-full bg-[var(--brand-forest-deep)] overflow-hidden"
    >
      <div
        data-testid="hero-banner"
        data-active-slide={heroIdx}
        className="relative w-full h-[75vh] overflow-hidden"
      >
        {HERO_SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            data-testid={`hero-slide-${i}`}
            className={
              'absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-in-out ' +
              (i === heroIdx ? 'opacity-100' : 'opacity-0')
            }
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}

        {/* Estate mark overlay — quietly on the image, top-left. Tiny.
            Anchors the brand without competing with the sunset. */}
        <div className="absolute top-4 left-6 lg:top-6 lg:left-10 z-10 flex items-center gap-3 text-[var(--brand-parchment)] [text-shadow:0_1px_10px_rgba(0,0,0,0.55)]">
          <span className="font-eyebrow !text-[var(--brand-gold)]">The Estate Mark · Since the 1920s</span>
          <span className="hidden sm:flex items-center gap-1 text-xs tracking-[0.2em] uppercase opacity-90">
            <MapPin size={12} /> Perthshire · A working life on the hill
          </span>
        </div>
      </div>

      {/*
        Content block on the parchment ground. Text is now readable
        against a solid brand backdrop rather than fighting a photograph —
        typography does the heavy lifting.
      */}
      <div className="relative bg-[var(--brand-parchment)]">
        {/* Faint grain over the parchment to keep the editorial feel */}
        <div aria-hidden="true" className="absolute inset-0 grain opacity-40 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-16 lg:pt-20 lg:pb-24">
          <div className="max-w-4xl">
            <h1
              className="rise rise-1 font-display text-[var(--brand-forest)] text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight"
            >
              Eco-Bikes &amp; Camping
              <br />
              <span className="italic text-[var(--brand-forest-deep)]">
                Supplies Co.
              </span>
            </h1>

            <p className="rise rise-2 mt-4 font-display italic text-[var(--brand-forest)] text-2xl lg:text-3xl">
              Gear that survives the Highlands.
            </p>

            <p className="rise rise-3 hero-copy mt-8 text-[var(--brand-ink)] text-lg lg:text-xl max-w-2xl leading-relaxed">
              Our story begins with{' '}
              <span className="italic text-[var(--brand-forest-deep)] font-medium">
                a Gamekeeper’s heirloom
              </span>{' '}
              — gifted to my great-great-grandfather by the Marquis of Bute for
              his years keeping the Ayrshire estate. I&rsquo;ve worked in
              Gamekeeping myself. E-bikes came to me over a decade ago,{' '}
              <span className="italic text-[var(--brand-forest-deep)] font-medium">
                so I&rsquo;ll be out on the hill for a while yet.
              </span>
            </p>

            {/* Practical terrain badge — the pivot into what our team
                actually knows: kickstands that hold on soft ground,
                walk-mode for pushing up stiles, frames you can swing
                on in waterproofs. */}
            <div
              data-testid="hero-terrain-badge"
              className="rise rise-3 mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full border border-[var(--brand-forest)]/25 bg-white px-4 py-2 text-xs sm:text-sm tracking-[0.18em] uppercase text-[var(--brand-forest)]"
            >
              <span
                aria-hidden="true"
                className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--brand-gold)]"
              />
              Practical Terrain &amp; Utility Advice
              <span className="opacity-70">·</span>
              <span className="opacity-90 normal-case tracking-normal">
                Robust kickstand &middot; 4&nbsp;kph walk mode &middot; Waterproof-friendly frames
              </span>
            </div>

            <div className="rise rise-4 mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid={HOME.heroPrimaryCta}
                onClick={() => go('contact')}
                aria-label="Talk with our team about which bike is best for your local terrain and your specific needs"
                className="btn-gold px-7 py-3.5 text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2 rounded-none"
              >
                Talk with our Team
                <ArrowRight size={16} />
              </button>
              <button
                data-testid={HOME.heroSecondaryCta}
                onClick={() => go('ebikes')}
                className="px-7 py-3.5 text-sm font-semibold tracking-wide rounded-none border border-[var(--brand-forest)]/40 text-[var(--brand-forest)] hover:bg-[var(--brand-forest)] hover:text-[var(--brand-parchment)] transition-colors"
              >
                Explore the E-Bikes
              </button>
            </div>
            <p className="rise rise-4 mt-3 text-sm italic text-[var(--brand-muted)] max-w-md">
              About which bike is best for your local terrain and your specific needs.
            </p>
          </div>

          <div className="rise rise-5 mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[var(--brand-stone)] pt-8 text-[var(--brand-ink)]">
            {[
              ['Ayrshire', 'Where the story begins'],
              ['A Gamekeeper’s Heirloom', 'Passed down the family'],
              ['4 kph Walk', 'Push through mud & stiles'],
              ['Kickstand', 'Holds a full pannier load'],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="font-display text-3xl text-[var(--brand-forest-deep)]">
                  {k}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.2em] opacity-90">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
